<template>
  <div class="edit-live-review-tracker">
    <div class="is-flex is-align-items-center is-justify-content-space-between mb-4">
      <h3 class="title is-6 mb-0">Réception des points</h3>
      <b-field class="mb-0">
        <b-select
          v-model="selectedStageId"
          expanded
          :disabled="!liveStages.length"
          :placeholder="liveStages.length ? 'Choisir une étape' : 'Aucune étape sur ce live'"
          icon="flag-checkered"
        >
          <option v-for="stage in liveStages" :key="stage.id" :value="stage.id">
            {{ stage.name }}
          </option>
        </b-select>
      </b-field>
    </div>

    <div class="columns is-desktop">
      <div class="column" :class="{ 'is-half-desktop': traces.length }">
        <edit-live-review-tracker-table
          v-model="selectedTrackerIds"
          :trackers="trackers"
          :loading="isTableLoading"
        />
      </div>
      <div v-if="traces.length" class="column">
        <edit-live-review-tracker-map :traces="traces" />
      </div>
    </div>
  </div>
</template>

<script>
import EditLiveReviewTrackerMap from './EditLiveReviewTrackerMap.vue'
import EditLiveReviewTrackerTable from './EditLiveReviewTrackerTable.vue'
import { DELAY_THRESHOLD_SECONDS } from './EditLiveReviewTrackerTraceLayer.vue'
import { mapActions } from 'vuex'

const median = (values) => {
  if (!values.length) {
    return null
  }

  const sorted = [...values].sort((left, right) => left - right)
  const middle = Math.floor(sorted.length / 2)

  if (sorted.length % 2 === 1) {
    return sorted[middle]
  }

  return Math.trunc((sorted[middle - 1] + sorted[middle]) / 2)
}

const computeDelayStatistics = (points) => {
  const delays = points
    .map(([, , locatedTs, createdTs]) => createdTs - locatedTs)
    .filter((delay) => delay >= 0)
  const delayedCount = delays.filter((delay) => delay > DELAY_THRESHOLD_SECONDS).length

  return {
    totalCount: points.length,
    delayedCount,
    medianDelaySeconds: delayedCount ? median(delays) : null,
  }
}

export default {
  name: 'EditLiveReviewTracker',

  components: {
    EditLiveReviewTrackerMap,
    EditLiveReviewTrackerTable,
  },

  props: {
    live: {
      type: Object,
      required: true,
    },
    liveVehicles: {
      type: Array,
      required: true,
    },
    liveStages: {
      type: Array,
      required: true,
    },
    queclinkTrackers: {
      type: Array,
      required: true,
    },
    spotTrackers: {
      type: Array,
      required: true,
    },
  },

  data() {
    return {
      isStatisticsLoading: false,
      isSeriesLoading: false,
      selectedStageId: null,
      selectedTrackerIds: [],
      liveStatistics: [],
      queclinkSeries: [],
      spotSeries: [],
    }
  },

  created() {
    this.abortController = null
    this.loadLiveStatistics()
    this.selectedStageId = this.liveStages.length ? this.liveStages[0].id : null
  },

  beforeDestroy() {
    this.abortSeries()
  },

  watch: {
    selectedStageId: 'loadSeries',
  },

  computed: {
    isTableLoading() {
      return this.isStatisticsLoading || this.isSeriesLoading
    },

    selectedStage() {
      return this.liveStages.find(({ id }) => id === this.selectedStageId) || null
    },

    labelsByTrackerId() {
      const bibs = Object.fromEntries(
        this.liveVehicles.flatMap((vehicle) =>
          [vehicle.queclinkTrackerId, vehicle.spotTrackerId]
            .filter(Boolean)
            .map((trackerId) => [trackerId, vehicle.number]),
        ),
      )

      return Object.fromEntries(
        [...this.queclinkTrackers, ...this.spotTrackers].map(({ id, number }) => [
          id,
          bibs[id] ? `${number} - ${bibs[id]}` : String(number),
        ]),
      )
    },

    stageStatisticsByTrackerId() {
      return Object.fromEntries(
        this.liveStatistics
          .filter((statistic) => statistic.liveStageId === this.selectedStageId)
          .map(({ queclinkTrackerId, batteryStatistics }) => [
            queclinkTrackerId,
            batteryStatistics,
          ]),
      )
    },

    trackers() {
      return [
        ...this.queclinkSeries.map(({ trackerId }) => {
          const statistics = this.stageStatisticsByTrackerId[trackerId]

          return {
            trackerId,
            label: this.labelsByTrackerId[trackerId],
            route: {
              name: 'queclinkTracker.edit',
              params: { id: trackerId, tab: 'statistics' },
            },
            totalCount: statistics ? statistics.totalCount : null,
            delayedCount: statistics ? statistics.delayedCount : null,
            medianDelaySeconds:
              statistics && statistics.delayedCount ? statistics.medianDelaySeconds : null,
          }
        }),
        ...this.spotSeries.map(({ trackerId, points }) => ({
          trackerId,
          label: this.labelsByTrackerId[trackerId],
          route: null,
          ...computeDelayStatistics(points),
        })),
      ]
    },

    pointsByTrackerId() {
      return Object.fromEntries(
        [...this.queclinkSeries, ...this.spotSeries].map(({ trackerId, points }) => [
          trackerId,
          points,
        ]),
      )
    },

    traces() {
      return this.selectedTrackerIds
        .map((trackerId) => ({
          trackerId,
          name: this.labelsByTrackerId[trackerId],
          points: this.pointsByTrackerId[trackerId] || [],
        }))
        .filter(({ points }) => points.length > 1)
    },
  },

  methods: {
    ...mapActions('ui', ['addToastMessage']),

    async loadLiveStatistics() {
      this.isStatisticsLoading = true

      try {
        this.liveStatistics =
          await this.$services.liveQueclinkTrackerStatisticsService.getAllByLiveId(this.live.id)
      } catch (error) {
        this.addToastMessage({ text: error.message, type: 'is-danger' })
      }

      this.isStatisticsLoading = false
    },

    abortSeries() {
      if (this.abortController) {
        this.abortController.abort()
        this.abortController = null
      }
    },

    async loadSeries() {
      this.abortSeries()

      if (!this.selectedStage) {
        this.queclinkSeries = []
        this.spotSeries = []
        this.isSeriesLoading = false

        return
      }

      const abortController = new AbortController()

      this.abortController = abortController
      this.isSeriesLoading = true

      const options = { signal: abortController.signal }

      await Promise.all([
        this.loadQueclinkSeries(abortController, options),
        this.loadSpotSeries(abortController, options),
      ])

      if (this.abortController === abortController) {
        this.abortController = null
        this.isSeriesLoading = false
      }
    },

    async loadQueclinkSeries(abortController, options) {
      const series = await this.collectSeries(
        this.$services.queclinkTrackerLocationSeriesService.getAllByLiveId(
          this.live.id,
          this.selectedStage.startedAt,
          this.selectedStage.endedAt,
          options,
        ),
        abortController,
      )

      if (this.abortController === abortController) {
        this.queclinkSeries = series
      }
    },

    async loadSpotSeries(abortController, options) {
      const series = await this.collectSeries(
        this.$services.spotTrackerLocationSeriesService.getAllByLiveId(
          this.live.id,
          this.selectedStage.startedAt,
          this.selectedStage.endedAt,
          options,
        ),
        abortController,
      )

      if (this.abortController === abortController) {
        this.spotSeries = series
      }
    },

    async collectSeries(stream, abortController) {
      const series = []

      try {
        for await (const batch of stream) {
          if (this.abortController !== abortController) {
            return series
          }

          series.push(...batch)
        }
      } catch (error) {
        if (this.abortController === abortController) {
          this.addToastMessage({ text: error.message, type: 'is-danger' })
        }
      }

      return series
    },
  },
}
</script>
